/* eslint-disable max-classes-per-file*/

import { Container } from 'aurelia-dependency-injection';
import { I18N } from 'aurelia-i18n';
import { v4 as uuidv4 } from 'uuid';

import { GenericApi, ParamType } from '~/api/generic-api';
import { Notification } from '~/components/notification/notification';
import { CommonURLParam } from '~/constants';
import { Logger } from '~/utils/logger';

/**
 * Typescript enumeration of HTTP request methods. Values taken from the java enum org.springframework.http.HttpMethod.
 */
export const enum HttpMethod {
	GET = 'GET',
	HEAD = 'HEAD',
	POST = 'POST',
	PUT = 'PUT',
	PATCH = 'PATCH',
	DELETE = 'DELETE',
	OPTIONS = 'OPTIONS',
	TRACE = 'TRACE',
}

class ExtensionContextUtils {
	static EXTENSION_CONTEXT_HEADER_KEY = 'X-Extension-Context';
	static EXTENSION_CONTEXT_HEADER_VALUE = 'GUI';
}

export interface OAuthErrorResponse {
	/**
	 * error_description is returned only for login/oauth responses from Spring.
	 */
	error_description?: string;
	/**
	 * error is an identifier string with the state of the oauth error.
	 * ie. 'invalid_client' | 'invalid_grant' | 'invalid_token'
	 */
	error?: string;
}

interface SpringBootErrorResponse {
	/**
	 * message is an optional nice error message from the backend.
	 */
	message?: string;
	/**
	 * error is a string representation of the status code returned or for oauth cases, its
	 */
	error?: string;
}

interface FetchSettings {
	disableInterceptors?: boolean;
	/**
	 * extractParamsFromURL will automatically extract parameters after the ? in
	 * a given fetch URL.
	 */
	extractParamsFromURL?: boolean;
}

/**
 * ResponseJSONError is a JSON response from the server that will render a normalized / nice error
 * message if cast to a string.
 *
 * This does not extend Error by design as we don't want any (resp instanceof Error) checks to capture
 * this kind of error message.
 */
// tslint:disable:max-classes-per-file
export class ResponseJSONError {
	[propName: string]: any;

	public toString(): string {
		return FetchUtils.normalizeError(this);
	}
}

/**
 * Interceptors can process requests before they are sent, and responses
 * before they are returned to callers.
 *
 * NOTE(Jae): 2020-04-02 - WEBUI-2597
 * This was copied out of the 'aurelia-fetch-client' dependency so that we could
 * utilize this with our existing auth-interceptor.ts implementation that was built
 * against this interface. Commented out and didn't implement
 * parts of the interface we don't utilize (we might want to uncomment / implement later)
 */
export interface Interceptor {
	/**
	 * Called with the request before it is sent. Request interceptors can modify and
	 * return the request, or return a new one to be sent. If desired, the interceptor
	 * may return a Response in order to short-circuit the HTTP request itself.
	 *
	 * @param request The request to be sent.
	 * @returns The existing request, a new request or a response; or a Promise for any of these.
	 */
	request?: (
		request: Request,
	) => Request | Response | Promise<Request | Response>;
	/**
	 * Handles errors generated by previous request interceptors. This function acts
	 * as a Promise rejection handler. It may rethrow the error to propagate the
	 * failure, or return a new Request or Response to recover.
	 *
	 * @param error The rejection value from the previous interceptor.
	 * @returns The existing request, a new request or a response; or a Promise for any of these.
	 */
	// requestError?: (error: any) => Request | Response | Promise<Request | Response>;
	/**
	 * Called with the response after it is received. Response interceptors can modify
	 * and return the Response, or create a new one to be returned to the caller.
	 *
	 * @param response The response.
	 * @returns The response; or a Promise for one.
	 */
	// response?: (response: Response, request?: Request) => Response | Promise<Response>;
	/**
	 * Handles fetch errors and errors generated by previous interceptors. This
	 * function acts as a Promise rejection handler. It may rethrow the error
	 * to propagate the failure, or return a new Response to recover.
	 *
	 * @param error The rejection value from the fetch request or from a
	 * previous interceptor.
	 * @returns The response; or a Promise for one.
	 */
	responseError?: (
		error: any,
		request?: Request,
	) => Response | Promise<Response>;
}

type ErrorResponse = OAuthErrorResponse & SpringBootErrorResponse;

type URLParameter =
	| string
	| number
	| boolean
	| URLParameterMap
	| URLParameterMap[]
	| undefined
	| null;

type URLParameterMap = { [param: string]: any } | undefined | null;

/**
 * The class holds the logic to create X-Correlation-ID, and register it in the given header.
 * The logic must be identical with the implementation in {@link CorrelationIdService.java} and
 * {@link CorrelationIdFilter.java}
 */
class CorrelationIdUtils {
	public static CORRELATION_ID_HEADER_NAME: string = 'X-Correlation-ID';
	private static ID_PREFIX: string = 'IR';

	/**
	 * To get the correlation id from the given response
	 * @param response to get the correlation id from the given response's header
	 */
	public static getIdFrom(response: Response): string | null {
		return response.headers.get(
			CorrelationIdUtils.CORRELATION_ID_HEADER_NAME,
		);
	}

	/**
	 * To compose the correlation id with the format of `IR-{UUID}`
	 * @private
	 * @return correlation id in string
	 */
	public static composeId(): string {
		return [CorrelationIdUtils.ID_PREFIX, uuidv4()].join('-');
	}
}

export class FetchUtils {
	private static baseUrl = '';

	private static interceptors: Interceptor[] = [];

	public static clear(): void {
		this.baseUrl = '';
		this.interceptors = [];
	}

	private static initHeaders() {
		return new Map([
			[
				ExtensionContextUtils.EXTENSION_CONTEXT_HEADER_KEY,
				ExtensionContextUtils.EXTENSION_CONTEXT_HEADER_VALUE,
			],
			[
				CorrelationIdUtils.CORRELATION_ID_HEADER_NAME,
				CorrelationIdUtils.composeId(),
			],
		]);
	}

	// set headers property to HeadersInit object in place
	private static setHeaders(
		headers: HeadersInit,
		property: string,
		value: string,
	): void {
		if (headers instanceof Headers) {
			headers.append(property, value);
		} else {
			headers[property] = value;
		}
	}
	public static getBaseUrl(): string {
		return this.baseUrl;
	}

	public static setBaseUrl(baseUrl: string) {
		if (DEVELOPMENT) {
			if (
				this.baseUrl &&
				// NOTE(Jae): 2020-04-07
				// If a user tries to change the baseUrl to the same value, we let it happen. This is so
				// Cypress tests don't have an error as it can call the configurationWithoutStart() method
				// multiple times.
				this.baseUrl !== baseUrl
			) {
				throw new Error(
					'DEVELOPMENT ERROR: Should not setBaseUrl() more than once. Should only be called once when initializing.',
				);
			}
		}
		this.baseUrl = baseUrl;
	}

	public static addInterceptor(interceptor: Interceptor): void {
		this.interceptors.push(interceptor);
	}

	/**
	 * isError will detect if a response is an error message.
	 */
	public static isError(error: Error | ErrorResponse): boolean {
		return Boolean(error instanceof Error || error.error);
	}

	public static async get(
		relativeUri: string,
		params?: URLParameterMap,
		settings?: FetchSettings | undefined,
	): Promise<Response> {
		const response = await this.fetch(
			relativeUri,
			params,
			{
				method: 'GET',
			},
			settings,
		);
		return response;
	}

	public static async fetchWithNoRequestBody(
		method: 'GET' | 'POST' | 'PUT' | HttpMethod,
		relativeUri: string,
		params?: URLParameterMap,
		settings?: FetchSettings | undefined,
	): Promise<Response> {
		const response = await this.fetch(
			relativeUri,
			params,
			{
				method,
			},
			settings,
		);
		return response;
	}

	public static async getJSON<T>(
		relativeUri: string,
		getParams?: URLParameterMap,
		settings?: FetchSettings,
	): Promise<T> {
		return this.fetchJSON(
			'GET',
			relativeUri,
			getParams,
			undefined,
			settings,
		);
	}

	public static async getJSONWithParamsInURL<T>(
		relativeUri: string,
	): Promise<T> {
		return this.getJSON(relativeUri, {}, { extractParamsFromURL: true });
	}

	// All queries are contained in the relativeUri, no body permitted in this method
	public static async fetchJSONWithParamsInURL<T>(
		method: 'GET' | 'POST' | 'PUT' | HttpMethod,
		relativeUri: string,
	): Promise<T> {
		return this.fetchJSON<T>(method, relativeUri, {}, undefined, {
			extractParamsFromURL: true,
		});
	}

	public static async fetchJSON<ResultType, BodyType = URLParameterMap>(
		method: 'GET' | 'POST' | 'PUT' | HttpMethod,
		relativeUri: string,
		getParams?: URLParameterMap,
		body?: BodyType,
		settings?: FetchSettings,
	): Promise<ResultType | undefined> {
		const response = await this.fetchWithRequestBody(
			method,
			relativeUri,
			getParams,
			body,
			settings,
		);
		if (response.headers.get('Content-Type') === null) {
			// NOTE(Jae): 2020-04-06 - WEBUI-2597
			// If we get an empty response, return undefined.
			// This is needed for the "credentials" API as it doesn't return anything
			// when you save/edit records.
			return undefined;
		}
		// Process JSON
		let content: ResultType;
		try {
			content = await response.json();
		} catch (e) {
			throw e;
		}
		return content;
	}

	public static async fetchResponseStatus<BodyType = URLParameterMap>(
		method: 'GET' | 'POST' | 'PUT' | HttpMethod,
		relativeUri: string,
		getParams?: URLParameterMap,
		body?: BodyType,
		settings?: FetchSettings,
	): Promise<number | undefined> {
		const response = await this.fetchWithRequestBody(
			method,
			relativeUri,
			getParams,
			body,
			settings,
		);
		return response.status;
	}

	public static async fetchWithRequestBody<BodyType = URLParameterMap>(
		method: 'GET' | 'POST' | 'PUT' | HttpMethod,
		relativeUri: string,
		getParams?: URLParameterMap,
		body?: BodyType,
		settings?: FetchSettings,
	): Promise<Response | undefined> {
		const headers = new Headers();
		// NOTE (York): 2021-07-30 - WEBUI-4050
		// This method is only used to get JSON HTTP response.
		headers.append('Accept', 'application/json');

		let requestBody: string | URLSearchParams | undefined;
		if (body instanceof URLSearchParams) {
			// URLSearchParams POST, i.e, /oauth/token API
			headers.append('Content-Type', 'application/x-www-form-urlencoded');
			requestBody = body;
		} else if (body !== undefined) {
			// Default body type is json.
			headers.append('Content-Type', 'application/json');
			requestBody = JSON.stringify(body);
		}

		const response = await this.fetch(
			relativeUri,
			getParams,
			{
				headers,
				method,
				body: requestBody,
			},
			settings,
		);
		await this.throwIfResponseErrors(response);
		return response;
	}

	private static async fetch(
		url: string,
		params: URLParameterMap,
		requestInit: RequestInit,
		settings: FetchSettings | undefined,
	): Promise<Response> {
		// Remove/extract query parameters from URL
		{
			const urlAndParams = url.split('?');
			const oldUrl = url;
			url = urlAndParams[0];
			if (urlAndParams.length > 1) {
				if (!settings || !settings.extractParamsFromURL) {
					throw new Error(
						'Cannot have query parameters in URL unless you use the FetchSetting "extractParamsFromURL".',
					);
				}
				// NOTE(Jae): 2020-04-06 - WEBUI-2597
				// I was hoping to not need to implement this kind of logic, but we currently
				// rely on this behaviour for "transaction-centre.ts" and its "Show My Transactions Only"
				// checkbox.
				const urlSearchParams = new URLSearchParams(urlAndParams[1]);
				const urlParams = {};
				urlSearchParams.forEach((value, key) => {
					urlParams[key] = value;
				});
				// Prepend the extract query parameters from the url *before*
				// the provided parameters
				params = {
					...urlParams,
					...params,
				};
			}
		}

		if (
			url &&
			// tslint:disable-next-line:no-http-string
			!url.startsWith('http://') &&
			// tslint:disable-next-line:no-http-string
			!url.startsWith('https://')
		) {
			url = this.baseUrl + url;
		}

		// Generate URL
		{
			// NOTE(Jae): 2020-04-01
			// We might want/need to move these to an interceptor or similar
			// so our business logic of this project is decoupled from this file.
			// Alternatively we could add a flag to "FetchSettings" to not add these
			// opt-out of these globals.
			const defaultParams = {};
			if (GenericApi.formName) {
				defaultParams[ParamType.FORM] = GenericApi.formName;
			}
			if (GenericApi.customisationName) {
				defaultParams[ParamType.CUSTOMISATION] =
					GenericApi.customisationName;
			}
			const division = GenericApi.getDivision();
			if (division !== undefined) {
				defaultParams[ParamType.DIVISION] = division;
			}
			const batchId = GenericApi.getBatchId();
			if (batchId) {
				defaultParams[ParamType.BATCH_ID] = batchId;
			}
			// WEBUI-3774 Parameters passed to pre-display for case360 screen pop
			this.populateCase360ParamsIfPresent(defaultParams);
			url = this.buildUriAndParams(url, params, defaultParams);
		}
		url = url.replace(/\/+$/, ''); // remove all trailing '/' characters.

		if (!requestInit.headers) {
			requestInit.headers = new Headers();
		}
		this.initHeaders().forEach((value, key) => {
			this.setHeaders(requestInit.headers, key, value);
		});
		let request = new Request(url, requestInit);
		let response: Response | undefined;
		if (!settings || !settings.disableInterceptors) {
			let responseCount = 0;
			for (const interceptor of this.interceptors) {
				if (!interceptor.request) {
					continue;
				}
				const result = await interceptor.request(request);
				if (result instanceof Request) {
					request = result;
				}
				if (result instanceof Response) {
					response = result;
					responseCount++;
				}
			}
			if (DEVELOPMENT && responseCount > 1) {
				throw new Error(
					'Should not have multiple interceptors that return responses at the same time in "request" method.',
				);
			}
		}
		// Ignore response if the interceptor has already populated it
		if (response === undefined) {
			try {
				response = await fetch(request);
			} catch (e) {
				if (settings && settings.disableInterceptors) {
					throw e;
				}
				response = await FetchUtils.handleErrorWithInterceptors(
					e,
					request,
				);
				if (response === e) {
					// If no interceptors modified the response,
					// throw the original error.
					throw e;
				}
			}
		}
		if (!response.ok && (!settings || !settings.disableInterceptors)) {
			response = await FetchUtils.handleErrorWithInterceptors(
				response,
				request,
			);
		}
		return response;
	}

	private static populateCase360ParamsIfPresent(defaultParams) {
		const urlParams = new URLSearchParams(
			window.location.hash.split('?')[1],
		);

		const customData = urlParams.get(CommonURLParam.CUSTOM_DATA);
		if (customData !== undefined) {
			defaultParams[CommonURLParam.CUSTOM_DATA] = customData;
		}
		const caseEntityType = urlParams.get(CommonURLParam.ENTITY_TYPE);
		if (caseEntityType !== undefined) {
			defaultParams[CommonURLParam.ENTITY_TYPE] = caseEntityType;
		}
		const caseEntityId = urlParams.get(CommonURLParam.ENTITY_ID);
		if (caseEntityId !== undefined) {
			defaultParams[CommonURLParam.ENTITY_ID] = caseEntityId;
		}
		const caseCaseId = urlParams.get(CommonURLParam.CASE_ID);
		if (caseCaseId !== undefined) {
			defaultParams[CommonURLParam.CASE_ID] = caseCaseId;
		}
	}

	/**
	 * @throws Error | ResponseJSONError
	 */
	private static async throwIfResponseErrors(
		response: Response | undefined,
	): Promise<void> {
		if (response === undefined) {
			// No response
			throw new Error('Failed to fetch');
		}
		if (response instanceof Response && !response.ok) {
			const contentTypeAndParams: string | null =
				response.headers.get('Content-Type');
			if (contentTypeAndParams === null) {
				// If no Content-Type returned
				throw new Error(
					String(response.status) + ' ' + response.statusText,
				);
			}
			const contentType = contentTypeAndParams.split(';')[0];
			if (contentType === 'application/json') {
				const json: { [propName: string]: any } = await response.json();
				// Fill ResponseJSONError with properties from response.
				// We instantiate a class so we can do "instanceof" checks at run-time to
				// detect the error type.
				const resp = new ResponseJSONError();
				for (const prop in json) {
					if (!json.hasOwnProperty(prop)) {
						continue;
					}
					resp[prop] = json[prop];
				}
				throw resp;
			}
			// If not JSON, assume its content text.
			const body: string = await response.text();
			if (body) {
				// Retain status on error if response contains text body so 500 responses
				// can be caught to prevent infinite login loops. This can happen when
				// authentication fails and runtime exceptions are thrown from the server.
				const error = new Error(body);
				(error as any).status = response.status;
				throw error;
			}
			throw new Error(
				String(response.status) + ' ' + response.statusText,
			);
		}
	}

	private static async handleErrorWithInterceptors(
		origResponse: Response,
		request: Request,
	): Promise<Response> {
		let responseCount = 0;
		let response: Response | Promise<Response>;
		for (const interceptor of this.interceptors) {
			if (!interceptor.responseError) {
				continue;
			}
			response = interceptor.responseError(origResponse, request);
			// If Promise, wait for it to process so we can
			// handle it below.
			if (response instanceof Promise) {
				try {
					// If promise, lets wait for it to be fulfilled.
					response = await response;
				} catch (e) {
					throw e;
				}
			}
			if (response === origResponse) {
				// If returned the original response, do nothing.
				continue;
			}
			if (response instanceof Response) {
				// Handle "retry" case and get first response
				responseCount++;
				continue;
			}
			throw new Error('Invalid result from interceptor "responseError"');
		}
		if (DEVELOPMENT && responseCount > 1) {
			throw new Error(
				'Should not have multiple interceptors that return responses at the same time in "responseError" method.',
			);
		}
		return response;
	}

	/**
	 * logAndDisplayError is a convenience function that can be used directly with
	 * promise "catch" functions.
	 */
	public static logAndDisplayError(
		error: Error | ErrorResponse | string | string[],
	): void {
		const errorString = FetchUtils.normalizeError(error);
		Notification.error(errorString);
		Logger.error(errorString);
	}

	/**
	 * normalizeError will take in various error formats and output it a clean string.
	 *
	 * This function will never return an empty string and always provide at least a default
	 * error message.
	 */
	public static normalizeError(
		error: Error | ErrorResponse | string | string[],
	): string {
		if (Array.isArray(error)) {
			// NOTE: Jae: 2020-02-04
			// This codepath is used for AcurityFunction "errorMessages" or similar
			error = error.join('\n');
		}
		if (typeof error === 'string' && error !== '') {
			return error;
		}
		if (typeof error === 'object') {
			if (error instanceof Error) {
				// NOTE: Jake: 2019-01-07
				// We use "message" instead of "toString()" so that "Error: " isn't
				// prefixed on the message.
				if (error.message) {
					return error.message;
				}
			} else {
				if (error.error_description) {
					return error.error_description;
				}
				if (error.message) {
					return error.message;
				}
				if (error.error) {
					return error.error;
				}
			}
		}
		// If unable to get a truthy error value above, return default error message
		const i18n = Container.instance.get(I18N);
		return i18n.tr('errors.system-error');
	}

	/**
	 * buildUriAndParams will replace values that match a parameter in "params" in the uri.
	 * "api/:formName/:customisationName", {formName: "my-form", customisationName: undefined}
	 *
	 * Any params that don't match an interpolation string will fallback to being appended as a query string. (ie. after ?)
	 */
	public static buildUriAndParams(
		uri: string,
		params: URLParameterMap,
		queryParams?: URLParameterMap,
	): string {
		let newParams: URLParameterMap | undefined;
		if (params !== undefined || params !== null) {
			// NOTE(Jake): 2019-10-30
			// Create a copy so we don't mutate the params object given.
			newParams = { ...params };
			let oldUri = uri;
			for (const paramName in newParams) {
				if (
					!Object.prototype.hasOwnProperty.call(newParams, paramName)
				) {
					continue;
				}
				const value = newParams[paramName];
				if (typeof value === 'object') {
					// Ignore objects for interpolation
					continue;
				}
				if (value === undefined) {
					// Ensure undefined just replaces with empty and not "undefined"
					uri = uri.replace(':' + paramName, '');
				} else {
					let encodedValue = encodeURIComponent(value);
					// NOTE(Jae): 2020-04-08 - WEBUI-2597
					// This replacement allows us to interpolate values with "/" in them.
					// For example, if someone provides a ":functionName" like interpolation
					// value like "adviser-details/adviser-dealer", this will ensure it doesn't
					// escape this 1 character.
					// While I think we mostly want this behaviour for our backend, we may need to add a flag
					// to FetchSettings to handle edge-cases in the future.
					// (ie. Imagine an path/ID string that needs a "/" like a URL)
					encodedValue = encodedValue.replace(/%2F/g, '/');
					uri = uri.replace(':' + paramName, encodedValue);
				}
				if (uri !== oldUri) {
					// If we replaced the param, remove from map
					// (so we don't append to queryParameters, ie. after the ?)
					delete newParams[paramName];
					oldUri = uri;
				}
			}
			let isEmpty = true;
			for (const paramName in newParams) {
				if (
					!Object.prototype.hasOwnProperty.call(newParams, paramName)
				) {
					continue;
				}
				isEmpty = false;
			}
			if (isEmpty) {
				newParams = undefined;
			}
		}
		if (newParams !== undefined || queryParams !== undefined) {
			// Make remaining parameters be appended to ?
			const combinedQueryParams = this.encodeQueryParams({
				...newParams,
				...queryParams,
			});
			if (combinedQueryParams !== '') {
				uri += '?' + combinedQueryParams;
			}
		}
		return uri;
	}

	/**
	 * Replacement for jQuery.param() function. This converts an input object to a url query parameter string.
	 * eg. {a:'1', b:['2','3'],c:{d:'4',e:'5'}} maps to a=1&b%5B0%5D=2&b%5B1%5D='3'&c.d=4&c.e=5
	 *
	 * This is code was taken from jQuery 3.4.1 and modified to support Spring Boot.
	 *
	 * @param obj
	 * @returns {string} the corresponding query parameter string.
	 */
	private static encodeQueryParams(params: URLParameterMap): string {
		if (params === null || params === undefined) {
			return '';
		}

		const s: string[] = [];
		const add = (key: string, valueOrFunction: any) => {
			// If value is a function, invoke it and use its return value
			const value =
				typeof valueOrFunction === 'function'
					? valueOrFunction()
					: valueOrFunction;
			if (value === undefined) {
				// Ignore undefined parameters.
				return;
			}
			s.push(
				encodeURIComponent(key) +
					'=' +
					encodeURIComponent(value == null ? '' : value),
			);
		};

		// If an array was passed in, assume that it is an array of form elements.
		if (Array.isArray(params)) {
			// Serialize the form elements
			for (const name in params) {
				if (!params.hasOwnProperty(name)) {
					continue;
				}
				const value = params[name];
				add(name, value);
			}
		} else {
			// Encode params recursively.
			for (const name in params) {
				if (!params.hasOwnProperty(name)) {
					continue;
				}
				const value = params[name];
				this.buildParams(name, value, add);
			}
		}
		if (!s || s.length === 0) {
			return '';
		}
		return s.join('&');
	}

	private static buildParams(
		prefix: string,
		obj: URLParameterMap | URLParameter,
		add: (key: string, value: any) => void,
	) {
		if (Array.isArray(obj)) {
			// Serialize array item.
			if (/\[\]$/.test(prefix)) {
				for (const v of obj) {
					add(prefix, v);
				}
			} else {
				// tslint:disable-next-line prefer-for-of
				for (let i = 0; i < obj.length; i++) {
					const v = obj[i];
					// Item is non-scalar (array or object), encode its numeric index.
					this.buildParams(
						prefix + '[' + (typeof v === 'object' ? i : '') + ']',
						v,
						add,
					);
				}
			}
		} else if (typeof obj === 'object') {
			// Serialize object item.
			for (const name in obj) {
				if (!obj.hasOwnProperty(name)) {
					continue;
				}
				// NOTE: Jake: 2019-09-05 - WEBUI-2465
				// This change is required to properly support Spring Boot.
				// See: https://stackoverflow.com/a/7203353/5013410
				this.buildParams(prefix + '.' + name, obj[name], add);
			}
		} else {
			// Serialize scalar item.
			add(prefix, obj);
		}
	}
}
